import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  App,
  Button,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Tooltip,
  Typography,
  type TableColumnsType,
  type TableProps,
} from 'antd';
import { BadgePlus, Edit3, Plus, RefreshCw, Search, Trash2, UserPlus } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ProTable from '@/components/pro/ProTable';
import { fmtTime } from '@/utils/utils';
import { useIamUserList } from '@/pages/iam/composables/useIamUsers';
import {
  useAddUserToGroup,
  useCreateIamUserGroup,
  useDeleteIamUserGroup,
  useIamUserGroupList,
  useIamGroupPolicies,
  useUpdateIamUserGroup,
} from '@/pages/iam/composables/useIamGroups';
import {
  useRevokePolicyGrant,
  useSavePolicyGrant,
} from '@/pages/iam/composables/useGrantPolicyData';
import { GrantPermissionDrawer, type GrantPermissionPreset } from '@/pages/iam/components';
import type { IamUserGroupRecord, ListIAMUserGroupsRequest } from '@/api/iam.api';
import type { IamUserListItem, ListIAMUsersRequest } from '@/types/iam.types';
import { debounce } from '@/utils/utils';
import './groups.css';

const PAGE_SIZE = 20;
type GroupSortField = 'member_count' | 'created_at';

const ALL_USERS_REQUEST: ListIAMUsersRequest = {
  page: 1,
  limit: -1,
  order_by: ['-created_at'],
  name: '',
  display_name: '',
};

type GroupModalState = { mode: 'create' | 'edit'; row?: IamUserGroupRecord } | null;

interface GroupFormValues {
  name: string;
  comment?: string;
  userIds?: string[];
  policyIds?: string[];
}

function groupDisplayName(group: IamUserGroupRecord): string {
  return group.display_name || group.name;
}

function userDisplayName(user: IamUserListItem): string {
  return user.name;
}

function resolveUserNames(
  userIds: string[],
  users: Array<Pick<IamUserListItem, 'id' | 'name'>>
): string[] {
  const userNameById = new Map(users.map((user) => [user.id, user.name]));

  // TODO: IAM 服务当前要求成员标识使用用户 name，而不是稳定的 user id；服务端修正后改回传 id。
  return userIds.map((userId) => {
    const userName = userNameById.get(userId);
    if (!userName) throw new Error(`未找到用户 ${userId} 的名称`);
    return userName;
  });
}

export default function GroupList() {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [keyword, setKeyword] = useState('');
  const [queryKeyword, setQueryKeyword] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [orderBy, setOrderBy] = useState<GroupSortField | `-${GroupSortField}`>('-created_at');

  const updateQueryKeyword = useMemo(
    () =>
      debounce((value: string) => {
        setQueryKeyword(value);
        setPage(1);
      }, 300),
    []
  );

  useEffect(() => () => updateQueryKeyword.cancel(), [updateQueryKeyword]);

  const apiQuery = useMemo<ListIAMUserGroupsRequest>(
    () => ({
      page,
      limit: pageSize,
      name: '',
      display_name: '',
      keyword: queryKeyword.trim(),
      order_by: [orderBy],
    }),
    [page, pageSize, queryKeyword, orderBy]
  );
  const { data, isLoading, isFetching, error, refetch } = useIamUserGroupList(apiQuery);
  const createGroup = useCreateIamUserGroup();
  const updateGroup = useUpdateIamUserGroup();
  const addUserToGroup = useAddUserToGroup();
  const deleteGroup = useDeleteIamUserGroup();
  const savePolicyGrant = useSavePolicyGrant();
  const revokePolicyGrant = useRevokePolicyGrant();

  const groups = useMemo(() => data?.items ?? [], [data]);

  const [groupModal, setGroupModal] = useState<GroupModalState>(null);
  const [addingMembersToGroup, setAddingMembersToGroup] = useState<IamUserGroupRecord>();
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [grantDrawer, setGrantDrawer] = useState<{ preset?: GrantPermissionPreset } | null>(null);
  const [groupForm] = Form.useForm<GroupFormValues>();
  const editingGroupId = groupModal?.mode === 'edit' ? groupModal.row?.id : undefined;
  const needsAllUsers = !!addingMembersToGroup || groupModal?.mode === 'edit';
  const { data: allUsersData } = useIamUserList(ALL_USERS_REQUEST, needsAllUsers);
  const {
    data: groupPolicies,
    isFetching: areGroupPoliciesFetching,
    error: groupPoliciesError,
  } = useIamGroupPolicies(editingGroupId, groupModal?.mode === 'edit');
  const users = useMemo(() => allUsersData?.items ?? [], [allUsersData]);
  const userOptions = useMemo(
    () =>
      users.map((user) => ({ value: user.id, label: `${userDisplayName(user)}（${user.id}）` })),
    [users]
  );
  const addUserOptions = useMemo(() => {
    const memberIds = new Set(addingMembersToGroup?.edges.users?.map((user) => user.id) ?? []);
    return userOptions.map((option) => ({ ...option, disabled: memberIds.has(option.value) }));
  }, [addingMembersToGroup, userOptions]);
  const policyOptions = useMemo(
    () =>
      (groupPolicies ?? []).map((policy) => ({
        value: policy.policy_id,
        label: `${policy.name} · ${policy.type === 'system' ? '系统策略' : '自定义策略'}`,
      })),
    [groupPolicies]
  );
  const grantedPolicyIds = useMemo(
    () =>
      (groupPolicies ?? []).filter((policy) => policy.is_grant).map((policy) => policy.policy_id),
    [groupPolicies]
  );

  const openGroupModal = useCallback(
    (row?: IamUserGroupRecord) => {
      setGroupModal({ mode: row ? 'edit' : 'create', row });
      groupForm.setFieldsValue({
        name: row ? groupDisplayName(row) : '',
        comment: row?.comment ?? '',
        userIds: row?.edges.users?.map((user) => user.id) ?? [],
      });
    },
    [groupForm]
  );

  useEffect(() => {
    if (searchParams.get('create') !== '1') return;
    openGroupModal();
    const next = new URLSearchParams(searchParams);
    next.delete('create');
    setSearchParams(next, { replace: true });
  }, [openGroupModal, searchParams, setSearchParams]);

  useEffect(() => {
    if (groupModal?.mode !== 'edit' || !groupPolicies) return;
    groupForm.setFieldValue('policyIds', grantedPolicyIds);
  }, [grantedPolicyIds, groupForm, groupModal?.mode, groupPolicies]);

  const saveGroup = async (values: GroupFormValues) => {
    const groupName = values.name.trim();
    setSubmitting(true);
    try {
      if (groupModal?.mode === 'edit' && groupModal.row) {
        const selectedPolicyIds = values.policyIds ?? [];
        const selectedPolicyIdSet = new Set(selectedPolicyIds);
        const grantedPolicyIdSet = new Set(grantedPolicyIds);
        const addedPolicyIds = selectedPolicyIds.filter(
          (policyId) => !grantedPolicyIdSet.has(policyId)
        );
        const removedPolicyIds = grantedPolicyIds.filter(
          (policyId) => !selectedPolicyIdSet.has(policyId)
        );

        await updateGroup.mutateAsync({
          id: groupModal.row.id,
          display_name: groupName,
          comment: values.comment?.trim() ?? '',
          users: resolveUserNames(values.userIds ?? [], [
            ...users,
            ...(groupModal.row.edges.users ?? []),
          ]),
        });
        await Promise.all([
          ...(addedPolicyIds.length
            ? [
                savePolicyGrant.mutateAsync({
                  policy_ids: addedPolicyIds,
                  identities: [{ identity_type: 'user_group', identity: groupModal.row.id }],
                }),
              ]
            : []),
          ...(removedPolicyIds.length
            ? [
                revokePolicyGrant.mutateAsync({
                  policy_ids: removedPolicyIds,
                  identities: [{ identity_type: 'user_group', identity: groupModal.row.id }],
                }),
              ]
            : []),
        ]);
        message.success('用户组已更新');
      } else {
        await createGroup.mutateAsync({
          name: groupName,
          display_name: groupName,
          comment: values.comment?.trim() ?? '',
        });
        message.success('用户组已创建');
      }
      setGroupModal(null);
    } finally {
      setSubmitting(false);
    }
  };

  const submitGroup = async () => {
    const values = await groupForm.validateFields();
    await saveGroup(values);
  };

  const removeGroup = (row: IamUserGroupRecord) => {
    deleteGroup.mutate(row.id, {
      onSuccess: () => message.success('用户组已删除'),
    });
  };

  const addUsersToGroup = async () => {
    if (!addingMembersToGroup || !selectedUserIds.length) return;
    setSubmitting(true);
    try {
      await addUserToGroup.mutateAsync({
        group_id: addingMembersToGroup.id,
        add_users: resolveUserNames(selectedUserIds, users),
        remove_users: [],
      });
      message.success(`已向 ${groupDisplayName(addingMembersToGroup)} 添加用户`);
      setAddingMembersToGroup(undefined);
      setSelectedUserIds([]);
    } finally {
      setSubmitting(false);
    }
  };

  const columns: TableColumnsType<IamUserGroupRecord> = [
    {
      title: '用户组',
      key: 'group',
      render: (_, row) => (
        <div className="iam-group-name-cell">
          <Typography.Text strong>{groupDisplayName(row)}</Typography.Text>
          <Typography.Text type="secondary">{row.comment}</Typography.Text>
        </div>
      ),
    },
    {
      title: '成员',
      key: 'member_count',
      width: 120,
      sorter: true,
      sortOrder:
        orderBy === 'member_count' ? 'ascend' : orderBy === '-member_count' ? 'descend' : null,
      sortDirections: ['descend', 'ascend', 'descend'],
      render: (_, row) => (
        <Typography.Text type="secondary">{row.edges.users?.length ?? 0} 人</Typography.Text>
      ),
    },
    {
      title: '创建时间',
      key: 'created_at',
      width: 160,
      sorter: true,
      sortOrder: orderBy === 'created_at' ? 'ascend' : orderBy === '-created_at' ? 'descend' : null,
      sortDirections: ['descend', 'ascend', 'descend'],
      render: (_, row) => fmtTime(row.created_at),
    },
    {
      title: '操作',
      width: 136,
      fixed: 'right',
      render: (_, row) => (
        <Space size={0} onClick={(e) => e.stopPropagation()}>
          <Tooltip title="新增授权">
            <Button
              type="text"
              aria-label={`为 ${groupDisplayName(row)} 新增授权`}
              icon={<BadgePlus size={15} />}
              onClick={() =>
                setGrantDrawer({ preset: { principalType: 'GROUP', principalId: row.id } })
              }
            />
          </Tooltip>
          <Tooltip title="添加用户">
            <Button
              type="text"
              aria-label={`向 ${groupDisplayName(row)} 添加用户`}
              icon={<UserPlus size={15} />}
              onClick={() => {
                setAddingMembersToGroup(row);
                setSelectedUserIds([]);
              }}
            />
          </Tooltip>
          <Tooltip title="编辑用户组">
            <Button
              type="text"
              aria-label={`编辑用户组 ${groupDisplayName(row)}`}
              icon={<Edit3 size={15} />}
              onClick={() => openGroupModal(row)}
            />
          </Tooltip>
          <Popconfirm
            title="删除用户组"
            description={`删除后将解除 ${row.edges.users?.length ?? 0} 名成员关系及组授权，成员已有的直接授权不受影响。`}
            okText="删除"
            okButtonProps={{ danger: true }}
            onConfirm={() => removeGroup(row)}
          >
            <Tooltip title="删除用户组">
              <Button
                type="text"
                danger
                aria-label={`删除用户组 ${groupDisplayName(row)}`}
                icon={<Trash2 size={15} />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handleTableChange: TableProps<IamUserGroupRecord>['onChange'] = (
    _pagination,
    _filters,
    sorter,
    extra
  ) => {
    if (extra.action !== 'sort') return;
    const activeSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    const field = activeSorter?.columnKey;
    if (!activeSorter?.order || (field !== 'member_count' && field !== 'created_at')) return;
    setOrderBy(activeSorter.order === 'ascend' ? field : `-${field}`);
    setPage(1);
  };

  return (
    <div className="pp-page iam-page">
      <header className="pp-head">
        <h2>用户组管理</h2>
        <Space>
          <Button icon={<RefreshCw size={14} />} onClick={() => refetch()}>
            刷新
          </Button>
          <Button type="primary" icon={<Plus size={14} />} onClick={() => openGroupModal()}>
            新建用户组
          </Button>
        </Space>
      </header>

      <div className="iam-group-toolbar">
        <Input
          aria-label="搜索关键词"
          className="iam-group-search"
          allowClear
          prefix={<Search size={14} />}
          placeholder="搜索用户组名称 / 用户组 ID"
          value={keyword}
          onChange={(e) => {
            const value = e.target.value;
            setKeyword(value);
            updateQueryKeyword(value);
          }}
        />
      </div>

      <ProTable<IamUserGroupRecord>
        data={groups}
        loading={isLoading}
        tableLoading={isFetching && !isLoading}
        error={error ? (error instanceof Error ? error.message : String(error)) : null}
        columns={columns}
        onChange={handleTableChange}
        rowKey="id"
        onRetry={refetch}
        pagination={{
          current: page,
          pageSize,
          total: data?.totalCount ?? 0,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50, 100],
          showTotal: (count) => `共 ${count} 条`,
          onChange: (nextPage, nextPageSize) => {
            setPage(nextPage);
            setPageSize(nextPageSize);
          },
        }}
        onRow={(row) => ({
          onClick: () => navigate(`/iam/groups/${row.id}`),
          style: { cursor: 'pointer' },
        })}
      />

      <Modal
        title={groupModal?.mode === 'edit' ? '编辑用户组' : '新建用户组'}
        open={!!groupModal}
        okText={groupModal?.mode === 'edit' ? '保存' : '创建'}
        confirmLoading={submitting}
        okButtonProps={{
          disabled:
            groupModal?.mode === 'edit' &&
            (areGroupPoliciesFetching || !!groupPoliciesError || !groupPolicies),
        }}
        onOk={submitGroup}
        onCancel={() => setGroupModal(null)}
        afterClose={() => groupForm.resetFields()}
        destroyOnHidden
      >
        <Form form={groupForm} layout="vertical">
          <Form.Item
            name="name"
            label="用户组名称"
            rules={[
              { required: true, message: '请输入用户组名称' },
              { min: 1, max: 64, message: '名称长度为 1-64 个字符' },
              {
                pattern: /^[一-龥A-Za-z0-9_-]+$/,
                message: '仅支持中英文、数字、连字符和下划线',
              },
            ]}
          >
            <Input placeholder="例如 Agent_开发组" />
          </Form.Item>
          <Form.Item name="comment" label="描述">
            <Input.TextArea rows={4} maxLength={200} showCount placeholder="说明用户组职责范围" />
          </Form.Item>
          {groupModal?.mode === 'edit' && (
            <>
              <Form.Item name="userIds" label="成员">
                <Select mode="multiple" allowClear options={userOptions} placeholder="选择成员" />
              </Form.Item>
              <Form.Item
                name="policyIds"
                label="继承策略"
                validateStatus={groupPoliciesError ? 'error' : undefined}
                help={
                  groupPoliciesError
                    ? groupPoliciesError instanceof Error
                      ? groupPoliciesError.message
                      : String(groupPoliciesError)
                    : undefined
                }
              >
                <Select
                  mode="multiple"
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  loading={areGroupPoliciesFetching}
                  disabled={!!groupPoliciesError}
                  options={policyOptions}
                  placeholder="选择成员可继承的策略"
                />
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>

      <Modal
        title={`添加用户 · ${addingMembersToGroup ? groupDisplayName(addingMembersToGroup) : ''}`}
        open={!!addingMembersToGroup}
        okText="确认添加"
        confirmLoading={submitting}
        okButtonProps={{ disabled: !selectedUserIds.length }}
        onOk={addUsersToGroup}
        onCancel={() => {
          setAddingMembersToGroup(undefined);
          setSelectedUserIds([]);
        }}
      >
        <Typography.Paragraph type="secondary">
          可一次选择多名用户；已经属于该用户组的成员不可重复选择。
        </Typography.Paragraph>
        <Select
          mode="multiple"
          showSearch
          optionFilterProp="label"
          style={{ width: '100%' }}
          placeholder="选择一个或多个用户"
          value={selectedUserIds}
          onChange={setSelectedUserIds}
          options={addUserOptions}
        />
      </Modal>

      <GrantPermissionDrawer
        open={!!grantDrawer}
        preset={grantDrawer?.preset}
        onClose={() => setGrantDrawer(null)}
        onSuccess={() => {
          void refetch();
        }}
      />
    </div>
  );
}
